import { Controller } from "@hotwired/stimulus"

export interface DateRangeSelection { start: string; end: string }

// Both endpoints are inclusive ISO dates. Consumers receive date-range-calendar:change;
// a widget given start/end field targets also writes the selection into them.
export default class extends Controller<HTMLElement> {
  static targets = ["month", "days", "day", "previous", "next", "hint", "pending", "startField", "endField"]
  static values = {
    start: String, end: String, today: String, minimum: String, maximum: String,
    disabled: Array, marks: Object, lockedStart: Boolean, pending: Boolean, locale: String,
  }
  declare startValue: string
  declare endValue: string
  declare readonly monthTarget: HTMLElement
  declare readonly daysTarget: HTMLElement
  declare readonly dayTarget: HTMLTemplateElement
  declare readonly previousTarget: HTMLButtonElement
  declare readonly nextTarget: HTMLButtonElement
  declare readonly hasNextTarget: boolean
  declare readonly hintTarget: HTMLElement
  declare readonly pendingTarget: HTMLElement
  declare readonly startFieldTarget: HTMLInputElement
  declare readonly endFieldTarget: HTMLInputElement
  declare readonly hasStartFieldTarget: boolean
  declare readonly todayValue: string
  declare readonly minimumValue: string
  declare readonly maximumValue: string
  declare readonly disabledValue: string[]
  declare readonly marksValue: Record<string, string>
  declare readonly lockedStartValue: boolean
  declare readonly pendingValue: boolean
  declare readonly localeValue: string
  private month!: Date
  private anchor?: string

  connect() {
    this.anchor = this.pendingValue && !this.lockedStartValue ? this.startValue : undefined
    let date = Date.parse(this.startValue)
    if (!Number.isFinite(date)) date = Date.parse(this.todayValue)
    date = Math.max(date, this.minimumDate)
    if (Number.isFinite(this.maximumDate)) date = Math.min(date, this.maximumDate)
    this.month = this.monthOf(new Date(date))
    this.render()
    this.element.hidden = false
  }

  previous() { this.changeMonth(-1) }
  next() { this.changeMonth(1) }
  minimumValueChanged() { this.rebound() }
  maximumValueChanged() { this.rebound() }
  disabledValueChanged() { this.rebound() }

  select(event: Event) {
    const date = (event.currentTarget as HTMLButtonElement).dataset.date!
    this.endValue = date
    if (this.lockedStartValue) {
      this.anchor = undefined
    } else if (this.anchor) {
      [this.startValue, this.endValue] = [this.anchor, date].sort()
      this.anchor = undefined
    } else {
      this.anchor = date
      this.startValue = date
    }
    this.render()
    this.focusDate(date)
    if (this.hasStartFieldTarget) {
      this.startFieldTarget.value = this.startValue
      this.endFieldTarget.value = this.endValue
    }
    this.dispatch("change", { detail: { start: this.startValue, end: this.endValue } satisfies DateRangeSelection })
  }

  moveFocus(event: KeyboardEvent) {
    const button = event.target as HTMLButtonElement
    if (!button.dataset.date) return
    const date = new Date(button.dataset.date)
    const offsets: Record<string, number> = {
      ArrowLeft: -1, ArrowRight: 1, ArrowUp: -7, ArrowDown: 7,
      Home: -date.getUTCDay(), End: 6 - date.getUTCDay(),
    }
    if (!(event.key in offsets)) return
    event.preventDefault()
    date.setUTCDate(date.getUTCDate() + offsets[event.key])
    if (date.getTime() < this.minimumDate || date.getTime() > this.maximumDate) return
    this.month = this.monthOf(date)
    this.render()
    this.focusDate(this.iso(date))
  }

  private get minimumDate() {
    const minimum = Date.parse(this.minimumValue)
    const start = this.lockedStartValue ? Date.parse(this.startValue) : NaN
    return Math.max(Number.isFinite(minimum) ? minimum : -Infinity, Number.isFinite(start) ? start : -Infinity)
  }

  private get maximumDate() {
    const maximum = Date.parse(this.maximumValue)
    return Number.isFinite(maximum) ? maximum : Infinity
  }

  private changeMonth(offset: number) {
    this.month.setUTCMonth(this.month.getUTCMonth() + offset)
    this.render()
  }

  // Bounds and disabled dates can move after connect: keep the shown month inside the
  // bounds and drop a selection they no longer allow.
  private rebound() {
    if (!this.month) return
    if (Number.isFinite(this.minimumDate)) this.month = new Date(Math.max(this.month.getTime(), this.monthOf(new Date(this.minimumDate)).getTime()))
    if (Number.isFinite(this.maximumDate)) this.month = new Date(Math.min(this.month.getTime(), this.monthOf(new Date(this.maximumDate)).getTime()))
    const { startValue: start, endValue: end } = this
    if (start && (Date.parse(start) < this.minimumDate || Date.parse(end) > this.maximumDate || this.disabledValue.some(iso => iso >= start && iso <= end))) {
      this.startValue = this.endValue = ""
      this.anchor = undefined
      if (this.hasStartFieldTarget) this.startFieldTarget.value = this.endFieldTarget.value = ""
    }
    this.render()
  }

  private render() {
    this.monthTarget.textContent = this.format(this.month, { month: "long", year: "numeric" })
    this.previousTarget.disabled = this.month <= this.monthOf(new Date(this.minimumDate))
    if (this.hasNextTarget) {
      this.nextTarget.disabled = Number.isFinite(this.maximumDate) && this.month >= this.monthOf(new Date(this.maximumDate))
    }
    this.hintTarget.hidden = !!this.anchor
    this.pendingTarget.hidden = !this.anchor
    const days = document.createDocumentFragment()
    for (let i = 0; i < this.month.getUTCDay(); i++) days.append(document.createElement("span"))
    const date = new Date(this.month)
    while (date.getUTCMonth() === this.month.getUTCMonth()) {
      days.append(this.dayButton(date))
      date.setUTCDate(date.getUTCDate() + 1)
    }
    this.daysTarget.replaceChildren(days)
    const focus = this.daysTarget.querySelector<HTMLButtonElement>('[aria-pressed="true"]:not(:disabled)') ||
      this.daysTarget.querySelector<HTMLButtonElement>('button:not(:disabled)')
    if (focus) focus.tabIndex = 0
  }

  private dayButton(date: Date) {
    const button = this.dayTarget.content.firstElementChild!.cloneNode(true) as HTMLButtonElement
    const iso = this.iso(date)
    const selected = !!this.startValue && iso >= this.startValue && iso <= this.endValue
    button.textContent = String(date.getUTCDate())
    button.dataset.date = iso
    button.dataset.edge = String(selected && (iso === this.startValue || iso === this.endValue))
    button.disabled = date.getTime() < this.minimumDate || date.getTime() > this.maximumDate || this.disabledValue.includes(iso)
    button.tabIndex = -1
    button.setAttribute("aria-label", this.format(date, { dateStyle: "full" }))
    button.setAttribute("aria-pressed", String(selected))
    if (iso === this.todayValue) button.setAttribute("aria-current", "date")
    if (this.marksValue[iso]) button.dataset.mark = this.marksValue[iso]
    return button
  }

  private focusDate(iso: string) {
    this.daysTarget.querySelectorAll<HTMLButtonElement>("button").forEach(button => {
      button.tabIndex = button.dataset.date === iso ? 0 : -1
      if (button.tabIndex === 0) button.focus()
    })
  }

  private monthOf(date: Date) { return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1)) }
  private iso(date: Date) { return date.toISOString().slice(0, 10) }
  private format(date: Date, options: Intl.DateTimeFormatOptions) {
    return new Intl.DateTimeFormat(this.localeValue, { ...options, timeZone: "UTC" }).format(date)
  }
}
